import Shepherd from 'tether-shepherd';

// CustomEvent polyfill for IE10/11 (from frontend-utils)
const CustomEvent = function(eventName, params) {
  const eventParams = { bubbles: false, cancelable: false, detail: undefined };

  for (let key in params) {
    if (params.hasOwnProperty(key)) {
      eventParams[key] = params[key];
    }
  }

  const customEvent = document.createEvent('CustomEvent');

  customEvent.initCustomEvent(
    eventName,
    eventParams.bubbles,
    eventParams.cancelable,
    eventParams.detail
  );

  return customEvent;
};

const TourFactory = function(element) {
  this.root = element;
  this.tourElements = Array.prototype.slice.apply(element.querySelectorAll('[data-tour]'));

  if (this.tourElements.length > 0) {
    this.tours = {};
    this.currentTourName = null;

    this.openers = Array.prototype.slice.apply(element.querySelectorAll('[data-tour-opener]'));

    const tourOverlayElement = document.createElement('div');

    tourOverlayElement.classList.add('tour-overlay');
    tourOverlayElement.classList.add('overlay-hidden');

    this.tourOverlay = document.body.appendChild(tourOverlayElement);

    this.initialize();

    // Open all tours without openers immediately
    if (this.openers.length < this.tourElements.length) {
      const that = this;
      const openerNames = that.openers.map(function(opener) {
        return opener.getAttribute('data-tour-opener');
      });

      that.tourElements.forEach(function(tourElement) {
        const tourName = tourElement.getAttribute('data-tour-name');
        if (!openerNames.includes(tourName)) {
          that.openTour(tourName);
        }
      });
    }
  }
};

TourFactory.prototype = {
  initialize: function() {
    const that = this;

    that.tourElements.forEach(function(tourElement) {
      that.initializeTour(tourElement);
    });

    that.attachEvents();
  },
  initializeTour: function(tourElement) {
    const that = this;
    const tourName = tourElement.getAttribute('data-tour-name');

    const tour = new Shepherd.Tour({
      defaults: {
        showCancelLink: true,
        buttons: [
          {
            text: tourElement.getAttribute('data-tour-skip'),
            classes: 'btn-default',
            action: function() {
              that.closeTour(tourName);
            }
          },
          {
            text: tourElement.getAttribute('data-tour-next'),
            classes: 'btn-primary',
            action: function() {
              that.clickNext(tourName);
            }
          }
        ]
      }
    });

    that.tours[tourName] = {
      tour: tour,
      name: tourName
    };
    that.addSteps(tour, tourElement);
  },
  addSteps: function(tour, tourElement) {
    const that = this;

    const steps = Array.prototype.slice.apply(tourElement.querySelectorAll('[data-tour-step]'));
    const sortedSteps = steps.sort(function(a, b) {
      const stepA = parseInt(a.getAttribute('data-step-number'), 10);
      const stepB = parseInt(b.getAttribute('data-step-number'), 10);

      if (stepA > stepB) {
        return 1;
      } else if (stepA < stepB) {
        return -1;
      } else {
        return 0;
      }
    });

    sortedSteps.forEach(function(step, index) {
      const stepConfig = {
        title: step.getAttribute('data-title') || '',
        text: step.innerHTML
      };

      const classes = step.getAttribute('data-classes') || '';

      const attachToElement = step.getAttribute('data-attach-to-element');
      const attachToPosition = step.getAttribute('data-attach-to-position');
      const positionOffset = {
        left: '0 25px',
        right: '0 -25px',
        top: '25px 0',
        bottom: '-25px 0'
      }[attachToPosition];

      if (classes) {
        stepConfig.classes = classes.split(' ');
      }

      if (attachToElement && attachToPosition && positionOffset) {
        stepConfig.attachTo = {
          element: attachToElement,
          on: attachToPosition
        };

        stepConfig.tetherOptions = {
          offset: positionOffset
        };
      }

      if (sortedSteps.length - 1 === index) {
        stepConfig.buttons = [
          {
            text: tourElement.getAttribute('data-tour-done'),
            classes: 'btn-primary',
            action: function() {
              const tourName = tourElement.getAttribute('data-tour-name');
              that.clickDone(tourName);
            }
          }
        ];
      }

      tour.addStep(stepConfig);

      tour.on('active', function() {
        that.tourOverlay.classList.remove('overlay-hidden');
      });

      tour.on('inactive', function() {
        that.tourOverlay.classList.add('overlay-hidden');
      });
    });
  },
  attachEvents: function() {
    const that = this;

    that.openers.forEach(function(opener) {
      const clickHandler = that.openTour.bind(that, opener.getAttribute('data-tour-opener'));
      opener.addEventListener('click', clickHandler);
    }, that);

    document.addEventListener('keyup', function(event) {
      const key = event.which || event.keyCode;

      if (that.currentTourName === null) {
        return;
      }

      // ESC
      if (key === 27) {
        that.closeTour(that.currentTourName);
      }
    });

    that.tourOverlay.addEventListener('click', function() {
      that.closeTour(that.currentTourName);
    });
  },
  openTour: function(tourName) {
    const tourObject = this.tours[tourName];

    this.currentTourName = tourObject.name;

    tourObject.tour.start();
    this.tourOverlay.classList.remove('overlay-hidden');
  },
  clickDone: function(tourName) {
    const tourObject = this.tours[tourName];
    const payload = {
      currentStep: tourObject.tour.getCurrentStep().id.replace('step-', ''),
      tourName: tourObject.name
    };
    const event = new CustomEvent('SOCRATA_STYLEGUIDE_TOUR_COMPLETE', { 'detail': payload });

    document.dispatchEvent(event);
    tourObject.tour.complete();
  },
  clickNext: function(tourName) {
    const tourObject = this.tours[tourName];
    const payload = {
      currentStep: tourObject.tour.getCurrentStep().id.replace('step-', ''),
      tourName: tourObject.name
    };

    document.dispatchEvent(new CustomEvent('SOCRATA_STYLEGUIDE_TOUR_NEXT', { 'detail': payload }));
    tourObject.tour.next();
  },
  closeTour: function(tourName) {
    const tourObject = this.tours[tourName];
    const payload = {
      currentStep: tourObject.tour.getCurrentStep().id.replace('step-', ''),
      tourName: tourObject.name
    };
    const event = new CustomEvent('SOCRATA_STYLEGUIDE_TOUR_CLOSED', { 'detail': payload });

    document.dispatchEvent(event);
    tourObject.tour.cancel();
  }
};

export default TourFactory;
